import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, Tag, Space, DatePicker,
  Typography, Descriptions, Drawer, Modal
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined, ReloadOutlined, GlobalOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const UPGRADE_STATUS = {
  pushed: { text: '已推送', color: 'processing' },
  upgraded: { text: '已升级', color: 'success' },
  fail: { text: '升级失败', color: 'error' },
  pending: { text: '待升级', color: 'default' },
};

const LANGUAGES = [
  { label: '英语 (美国)', key: 'en-US' },
  { label: '日语 (日本)', key: 'ja-JP' },
  { label: '德语 (德国)', key: 'de-DE' },
  { label: '法语 (法国)', key: 'fr-FR' },
  { label: '韩语 (韩国)', key: 'ko-KR' },
  { label: '葡萄牙语 (巴西)', key: 'pt-BR' },
];

const MOCK_TASKS = {
  '1': { fwName: '主控固件', fwVersion: 'V3.0.0', productName: '智能摄像机Q1', productId: 'PROD_Q1', upgradeMode: '静默升级 (01:00 - 05:00)', deviceScope: '全部设备' },
  '2': { fwName: 'WiFi模组固件', fwVersion: 'V2.0.0', productName: '云台机P1', productId: 'PROD_P1', upgradeMode: 'App提醒升级', deviceScope: '全部设备' },
  '3': { fwName: 'MCU固件', fwVersion: 'V2.1.0', productName: '智能摄像机Q1', productId: 'PROD_Q1', upgradeMode: '静默升级 (02:00 - 06:00)', deviceScope: '指定设备: 3000个' },
};

const MOCK_DEVICES = [
  { key: '1', license: '000HMY260407ZZZZ', status: 'pending', pushCount: 2, failCount: 0, updatedAt: '2026-04-10 18:01:41' },
  { key: '2', license: '000HMY260407AAAA', status: 'pushed', pushCount: 3, failCount: 0, updatedAt: '2026-04-10 17:30:00' },
  { key: '3', license: '000HMY260407BBBB', status: 'upgraded', pushCount: 2, failCount: 0, updatedAt: '2026-04-10 18:15:22' },
  { key: '4', license: '000HMY260407CCCC', status: 'fail', pushCount: 4, failCount: 3, updatedAt: '2026-04-10 18:20:10' },
  { key: '5', license: '000HMY260407DDDD', status: 'upgraded', pushCount: 1, failCount: 0, updatedAt: '2026-04-10 17:50:00' },
];

const OtaDeviceDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('id') || '1';
  const task = MOCK_TASKS[taskId] || MOCK_TASKS['1'];
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [i18nVisible, setI18nVisible] = useState(false);

  const columns = [
    { title: '序号', width: 60, align: 'center', render: (_, __, i) => i + 1 },
    { title: 'License', dataIndex: 'license', width: 200 },
    {
      title: '最新状态', dataIndex: 'status', width: 120,
      render: (v) => { const s = UPGRADE_STATUS[v] || { text: v, color: 'default' }; return <Tag color={s.color}>{s.text}</Tag>; },
    },
    { title: '推送次数', dataIndex: 'pushCount', width: 100 },
    {
      title: '升级失败次数', dataIndex: 'failCount', width: 120,
      render: (v) => v > 0 ? <Text type="danger">{v}</Text> : 0,
    },
    { title: '更新时间', dataIndex: 'updatedAt', width: 180 },
    {
      title: '操作', key: 'action', width: 100,
      render: (_, r) => <Button type="link" size="small" onClick={() => { setDrawerRecord(r); setDrawerVisible(true); }}>查看详情</Button>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/device/firmware/ota')} />
        <Title level={4} style={{ margin: 0 }}>覆盖设备明细</Title>
      </div>

      {/* 任务摘要 */}
      <Card bordered={false} style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px 24px' }}>
          <div><Text type="secondary">固件名称</Text><div style={{ fontWeight: 500 }}>{task.fwName}</div></div>
          <div><Text type="secondary">固件版本</Text><div style={{ fontWeight: 500 }}>{task.fwVersion}</div></div>
          <div><Text type="secondary">产品名称</Text><div style={{ fontWeight: 500 }}>{task.productName}</div></div>
          <div><Text type="secondary">产品ID</Text><div style={{ fontWeight: 500 }}>{task.productId}</div></div>
          <div><Text type="secondary">升级方式</Text><div style={{ fontWeight: 500 }}>{task.upgradeMode}</div></div>
          <div><Text type="secondary">待升级设备</Text><div style={{ fontWeight: 500 }}>{task.deviceScope}</div></div>
          <div>
            <Text type="secondary">升级内容</Text>
            <div><Button size="small" onClick={() => setI18nVisible(true)}>查看</Button></div>
          </div>
        </div>
      </Card>

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12 }} bodyStyle={{ padding: '12px 0' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item name="license" style={{ marginRight: 8 }}>
            <Input placeholder="请输入License" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="status" style={{ marginRight: 8 }}>
            <Select placeholder="升级状态" allowClear style={{ width: 130 }}>
              <Option value="all">全部</Option>
              {Object.entries(UPGRADE_STATUS).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" style={{ marginRight: 8 }}>
            <RangePicker showTime placeholder={['开始时间', '结束时间']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 设备列表 */}
      <Card bordered={false}>
        <Table columns={columns} dataSource={MOCK_DEVICES} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 查看详情抽屉 */}
      <Drawer title="设备详情" open={drawerVisible} onClose={() => setDrawerVisible(false)} width={480}>
        {drawerRecord && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="License">{drawerRecord.license}</Descriptions.Item>
            <Descriptions.Item label="最新状态">
              {(() => { const s = UPGRADE_STATUS[drawerRecord.status] || { text: drawerRecord.status, color: 'default' }; return <Tag color={s.color}>{s.text}</Tag>; })()}
            </Descriptions.Item>
            <Descriptions.Item label="推送次数">{drawerRecord.pushCount}</Descriptions.Item>
            <Descriptions.Item label="升级失败次数">{drawerRecord.failCount > 0 ? <Text type="danger">{drawerRecord.failCount}</Text> : 0}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{drawerRecord.updatedAt}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* 升级内容弹窗 */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><GlobalOutlined style={{ color: '#1890ff' }} /><span>升级内容</span></div>}
        open={i18nVisible} onCancel={() => setI18nVisible(false)} footer={null} width={850} destroyOnClose
        bodyStyle={{ maxHeight: 600, overflowY: 'auto', padding: '12px 24px' }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>默认升级内容（中文）：</div>
          <div style={{ padding: '10px 16px', background: '#f5f5f5', borderRadius: 6, fontSize: 13, border: '1px solid #eee' }}>
            修复已知问题，提升设备稳定性
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px 24px' }}>
          {LANGUAGES.map(lang => (
            <div key={lang.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 500 }}>{lang.label}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>{lang.key}</Text>
              </div>
              <div style={{ padding: '10px 16px', background: '#f5f5f5', borderRadius: 6, fontSize: 13, border: '1px solid #eee', minHeight: 44 }}>
                {lang.key === 'en-US' ? 'Bug fixes and stability improvements' : '——'}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default OtaDeviceDetail;
